import { useRef } from "react";
import "./RegisterOrganization.scss";
import { Modal, Form, Typography, Input, Spin, Button, Divider } from "antd";
import { PhoneNumberInput } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { resetAuthError } from "../../features/auth/authSlice";
import registerOrgSchema from "../../validations/registerOrgValidation";
import { createYupValidator } from "../../utils/validationHelper";

export default function RegisterOrganization({
  token,
  title,
  isOpened,
  onCancel,
  onOk,
}) {
  const { error, loading } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const formRef = useRef(null);

  return (
    <Modal
      style={{
        borderTop: `2px solid ${token?.colorPrimary}`,
      }}
      className="registerOrg-cont"
      open={isOpened}
      title={title}
      centered={true}
      destroyOnHidden={true}
      onCancel={() => {
        formRef?.current?.resetFields();
        onCancel();
        if (error) {
          dispatch(resetAuthError());
        }
      }}
      footer={null}>
      <Spin spinning={loading} size="large">
        <Form
          className="registerOrg-cont__form"
          ref={formRef}
          layout="vertical"
          onFinish={(values) => {
            const filteredValues = Object.fromEntries(
              Object.entries(values).filter(([_, value]) => value)
            );
            onOk(
              filteredValues,
              () => {
                formRef?.current?.resetFields();
                dispatch(resetAuthError());
              },
              formRef
            );
          }}>
          <div className="registerOrg-cont__form__formItemWrapper">
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="name"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(registerOrgSchema, "name"),
                },
              ]}
              label={<Typography.Text>Organization Name</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Organization Name"
                onChange={() => {
                  if (error && error["name"]) {
                    dispatch(resetAuthError("name"));
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="email"
              rules={[
                {
                  required: true,
                  validator: createYupValidator(registerOrgSchema, "email"),
                },
              ]}
              label={<Typography.Text>Email</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Email"
                onChange={() => {
                  if (error && error["email"]) {
                    dispatch(resetAuthError("email"));
                  }
                }}
              />
            </Form.Item>
          </div>
          <div className="registerOrg-cont__form__formItemWrapper">
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="password"
              hasFeedback
              rules={[
                {
                  required: true,
                  validator: createYupValidator(registerOrgSchema, "password"),
                },
              ]}
              label={<Typography.Text>Password</Typography.Text>}
              colon={false}>
              <Input.Password
                placeholder="Password"
                onChange={() => {
                  if (error && error["password"]) {
                    dispatch(resetAuthError("password"));
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="password_confirm"
              dependencies={["password"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  validator: async () => {
                    try {
                      const formValues = formRef?.current?.getFieldsValue();
                      await registerOrgSchema?.validateAt(
                        "password_confirm",
                        formValues
                      );
                      return Promise.resolve();
                    } catch (err) {
                      return Promise.reject(err?.message);
                    }
                  },
                },
              ]}
              label={<Typography.Text>Confirm Password</Typography.Text>}
              colon={false}>
              <Input.Password
                placeholder="Confirm Password"
                onChange={() => {
                  if (error && error["password_confirm"]) {
                    dispatch(resetAuthError("password_confirm"));
                  }
                }}
              />
            </Form.Item>
          </div>
          <Divider
            style={{
              margin: "8px 0",
            }}
            type="horizontal"
          />

          <Typography.Text
            type="secondary"
            style={{
              display: "block",
              marginBottom: "12px",
            }}>
            Optional Information
          </Typography.Text>
          <div className="registerOrg-cont__form__formItemWrapper">
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="phone_number"
              rules={[
                {
                  validator: createYupValidator(
                    registerOrgSchema,
                    "phone_number"
                  ),
                },
              ]}
              label={<Typography.Text>Phone Number</Typography.Text>}
              colon={false}>
              <PhoneNumberInput
                onChange={() => {
                  if (error && error["phone_number"]) {
                    dispatch(resetAuthError("phone_number"));
                    formRef?.current?.setFields([
                      {
                        name: "phone_number",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="license_number"
              rules={[
                {
                  validator: createYupValidator(
                    registerOrgSchema,
                    "license_number"
                  ),
                },
              ]}
              label={<Typography.Text>License Number</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="License Number"
                onChange={() => {
                  if (error && error["license_number"]) {
                    dispatch(resetAuthError("license_number"));
                    formRef?.current?.setFields([
                      {
                        name: "license_number",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
          </div>
          <div className="registerOrg-cont__form__formItemWrapper">
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="address"
              rules={[
                {
                  validator: createYupValidator(registerOrgSchema, "address"),
                },
              ]}
              label={<Typography.Text>Address</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Address"
                onChange={() => {
                  if (error && error["address"]) {
                    dispatch(resetAuthError("address"));
                    formRef?.current?.setFields([
                      {
                        name: "address",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="state"
              rules={[
                {
                  validator: createYupValidator(registerOrgSchema, "state"),
                },
              ]}
              label={<Typography.Text>State</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="State"
                onChange={() => {
                  if (error && error["state"]) {
                    dispatch(resetAuthError("state"));
                    formRef?.current?.setFields([
                      {
                        name: "state",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
          </div>
          <div className="registerOrg-cont__form__formItemWrapper">
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="city"
              rules={[
                {
                  validator: createYupValidator(registerOrgSchema, "city"),
                },
              ]}
              label={<Typography.Text>City</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="City"
                onChange={() => {
                  if (error && error["city"]) {
                    dispatch(resetAuthError("city"));
                    formRef?.current?.setFields([
                      {
                        name: "city",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              className="registerOrg-cont__form__formItemWrapper__formItem"
              name="zipcode"
              rules={[
                {
                  validator: createYupValidator(registerOrgSchema, "zipcode"),
                },
              ]}
              label={<Typography.Text>Zip Code</Typography.Text>}
              colon={false}>
              <Input
                type="text"
                placeholder="Zip Code"
                onChange={() => {
                  if (error && error["zipcode"]) {
                    dispatch(resetAuthError("zipcode"));
                    formRef?.current?.setFields([
                      {
                        name: "zipcode",
                        errors: [],
                      },
                    ]);
                  }
                }}
              />
            </Form.Item>
          </div>
          <Button
            className="registerOrg-cont__form__btn"
            type="primary"
            block
            htmlType="submit"
            disabled={error ? Object.keys(error)?.length !== 0 : false}
            loading={loading}>
            Register
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
}
